import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthStore } from '@/features/auth'
import { getLastActivity, recordActivity, IDLE_TIMEOUT_MS } from '../lib/activity'

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const
const WARNING_MS = 60_000
const THROTTLE_MS = 1_000

/**
 * Logs the user out after IDLE_TIMEOUT_MS without interaction,
 * showing a warning during the final minute.
 */
export const useIdleLogout = () => {
    const [warningOpen, setWarningOpen] = useState(false)
    const [secondsLeft, setSecondsLeft] = useState(Math.ceil(WARNING_MS / 1000))
    const logout = useAuthStore((s) => s.logout)
    const lastRecordRef = useRef(0)
    // Guards against the 1s interval (or a focus/visibility check) calling
    // logout() repeatedly once the threshold has passed.
    const loggedOutRef = useRef(false)

    useEffect(() => {
        recordActivity()
        loggedOutRef.current = false

        const checkIdle = () => {
            if (loggedOutRef.current) return
            const remaining = IDLE_TIMEOUT_MS - (Date.now() - getLastActivity())
            if (remaining <= 0) {
                loggedOutRef.current = true
                logout()
            } else if (remaining <= WARNING_MS) {
                setSecondsLeft(Math.ceil(remaining / 1000))
                setWarningOpen(true)
            } else {
                setWarningOpen(false)
            }
        }

        const onActivity = () => {
            const now = Date.now()
            if (now - lastRecordRef.current < THROTTLE_MS) return
            lastRecordRef.current = now
            recordActivity()
        }
        ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }))
        // Background tabs throttle setInterval, so the timer can lag far behind.
        // Re-check the moment the tab regains focus/visibility — if the user
        // crossed the threshold while away, log out immediately rather than
        // waiting for the next (throttled) tick.
        const onVisibility = () => {
            if (document.visibilityState === 'visible') checkIdle()
        }
        document.addEventListener('visibilitychange', onVisibility)
        window.addEventListener('focus', checkIdle)

        const tick = setInterval(checkIdle, 1000)

        return () => {
            ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity))
            document.removeEventListener('visibilitychange', onVisibility)
            window.removeEventListener('focus', checkIdle)
            clearInterval(tick)
        }
    }, [logout])

    const stayLoggedIn = useCallback(() => {
        recordActivity()
        setWarningOpen(false)
    }, [])

    return { warningOpen, secondsLeft, stayLoggedIn }
}
